import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Phone } from 'lucide-react';
const PROJECTS = [{
  src: 'https://cdn.magicpatterns.com/uploads/xfY67kC7BzdyJbTD1n2oZh/yei3.png',
  title: 'Wood Deck Restoration',
  tag: 'Before & After'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/fJP4hRBDZSRkM5MCpRfgmV/yei2.png',
  title: 'Lawn Care & Landscaping',
  tag: 'Lawn Maintenance'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/rhJW4SuRS2WFWN6wSLxFWJ/yei5.png',
  title: 'Fence Installation',
  tag: 'Fencing'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/9rjMbgEqgpmgbpCmXMP5uw/yei6.png',
  title: 'Palm Tree Trimming',
  tag: 'Tree Trimming'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/fc738DAXgjtbmQMZRVjD1F/yei7.png',
  title: 'Hardscape & Rock Design',
  tag: 'Hardscape'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/dX4g8iufk9A2HaSJAcERFe/yei8.png',
  title: 'Irrigation System Install',
  tag: 'Irrigation'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/5ieTXMHYVAdMBUgNAyw5Dx/1111.png',
  title: 'Front Yard Landscaping',
  tag: 'Landscaping'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/hDRC4uK1rPKfY3hFo6DGFw/coast6.png',
  title: 'Professional Tree Service',
  tag: 'Tree Service'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/nnrfEGTj9UEQBteMBM3rv7/junk4.png',
  title: 'Retaining Wall Construction',
  tag: 'Hardscape'
}];
export function BeforeAfter() {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);
  const goTo = useCallback((index: number, dir: number) => {
    setDirection(dir);
    setCurrent(index);
  }, []);
  const next = useCallback(() => {
    goTo((current + 1) % PROJECTS.length, 1);
  }, [current, goTo]);
  const prev = useCallback(() => {
    goTo((current - 1 + PROJECTS.length) % PROJECTS.length, -1);
  }, [current, goTo]);
  // Auto-advance every 5 seconds
  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);
  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 300 : -300,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -300 : 300,
      opacity: 0
    })
  };
  return <section className="py-20 bg-lightGray" data-id="element-15">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-16">
        {/* Header */}
        <div className="text-center mb-12" data-id="element-17">
          <span className="text-primary font-bold tracking-wider text-sm uppercase mb-3 block" data-id="element-18">
            OUR WORK
          </span>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-darkCharcoal mb-4" data-id="element-19">
            Real Work. Real Transformations.
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-20">
            See what YEI Mateo Lawn Care can do for your property.
          </p>
        </div>

        {/* Carousel */}
        <div className="relative bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-200" data-id="element-21">
          {/* Project Tag */}
          <div className="bg-primary px-6 py-4 flex items-center justify-between" data-id="element-22">
            <h3 className="text-white font-bold text-lg" data-id="element-23">
              {PROJECTS[current].title}
            </h3>
            <span className="bg-white/20 text-white text-sm font-semibold px-3 py-1 rounded-full" data-id="element-24">
              {PROJECTS[current].tag}
            </span>
          </div>

          {/* Image Area */}
          <div className="relative aspect-[16/10] md:aspect-[16/9] overflow-hidden bg-gray-100" data-id="element-25">
            <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-26">
              <motion.img key={current} src={PROJECTS[current].src} alt={PROJECTS[current].title} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.4,
              ease: 'easeInOut'
            }} className="absolute inset-0 w-full h-full object-cover" data-id="element-27" />
            </AnimatePresence>

            {/* Navigation Arrows */}
            <button onClick={prev} className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-darkCharcoal w-12 h-12 rounded-full flex items-center justify-center shadow-md transition-colors z-10" aria-label="Previous project" data-id="element-28">
              <ChevronLeft size={24} data-id="element-29" />
            </button>
            <button onClick={next} className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-darkCharcoal w-12 h-12 rounded-full flex items-center justify-center shadow-md transition-colors z-10" aria-label="Next project" data-id="element-30">
              <ChevronRight size={24} data-id="element-31" />
            </button>

            {/* Dots */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10" data-id="element-32">
              {PROJECTS.map((_, i) => <button key={i} onClick={() => goTo(i, i > current ? 1 : -1)} className={`w-3 h-3 rounded-full transition-all ${i === current ? 'bg-white scale-110 shadow' : 'bg-white/50 hover:bg-white/70'}`} aria-label={`Go to project ${i + 1}`} data-id="element-33" />)}
            </div>
          </div>
        </div>

        {/* Thumbnail Strip */}
        <div className="mt-6 grid grid-cols-3 sm:grid-cols-6 gap-3" data-id="element-34">
          {PROJECTS.map((project, i) => <button key={i} onClick={() => goTo(i, i > current ? 1 : -1)} className={`relative rounded-xl overflow-hidden aspect-square border-2 transition-all ${i === current ? 'border-primary shadow-md scale-105' : 'border-transparent opacity-70 hover:opacity-100'}`} data-id="element-35">
              <img src={project.src} alt={project.title} className="w-full h-full object-cover" data-id="element-36" />
            </button>)}
        </div>

        {/* Bottom CTA */}
        <div className="mt-10 text-center" data-id="element-37">
          <p className="text-darkCharcoal font-semibold text-lg mb-4" data-id="element-38">
            Want results like this for your property?
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4" data-id="element-39">
            <a href="#contact" className="bg-primary hover:bg-primary/90 text-white font-bold py-3 px-8 rounded-md transition-colors w-full sm:w-auto text-center" data-id="element-40">
              GET FREE ESTIMATE
            </a>
            <a href="tel:6192492966" className="inline-flex items-center justify-center gap-2 text-primary font-bold hover:text-primary/80 transition-colors w-full sm:w-auto" data-id="element-41">
              <Phone size={20} data-id="element-42" />
              (619) 249-2966
            </a>
          </div>
        </div>
      </div>
    </section>;
}